import type { Automation } from "@repo/app-types/entities/automation";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { iconButton } from "@repo/ui/Buttons/iconButton";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { popoverStyle } from "@repo/ui/popover/style";
import {
  IconCalendar,
  IconClock,
  IconInfoCircle,
  IconPlayerPlay,
  IconUser,
} from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { Button, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { AutosaveStatus } from "../../hooks/useAutosaveDraft.js";
import { formatRelativeTime } from "../../hooks/usePlaybookSaveStatus.js";
import { describeAutomationNextRun } from "./automationTriggerSummary.js";

const SLOW_SAVE_THRESHOLD_MS = 3000;

/**
 * Returns true once `active` has stayed true continuously for `delayMs`.
 * Used to reveal a "still saving" spinner only when a save runs long.
 */
function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [isElapsed, setIsElapsed] = useState(false);

  useEffect(() => {
    if (!active) {
      setIsElapsed(false);
      return;
    }
    const timeout = setTimeout(() => {
      setIsElapsed(true);
    }, delayMs);
    return () => {
      clearTimeout(timeout);
    };
  }, [active, delayMs]);

  return isElapsed;
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export interface AutomationInfoPopoverProps {
  automation: Automation;
  saveStatus: AutosaveStatus;
  lastSavedAt: string | null;
  createdByName: string | undefined;
  /** Opens the popover initially (for stories/tests). */
  defaultOpen?: boolean;
}

/**
 * Header info button + popover summarizing the automation: the live save state (which
 * replaces the old inline "Saving…" indicator), the next scheduled run, and who
 * created it and when. While a save runs longer than {@link SLOW_SAVE_THRESHOLD_MS}
 * the icon becomes a spinner; a failed save shows a red dot. Presentational —
 * the creator's name is resolved by the caller.
 */
export const AutomationInfoPopover: FC<AutomationInfoPopoverProps> = (
  props,
) => {
  const { automation, saveStatus, lastSavedAt, createdByName, defaultOpen } =
    props;
  const isSaving = saveStatus === "saving";
  const isSlowSaving = useDelayedFlag(isSaving, SLOW_SAVE_THRESHOLD_MS);
  const hasError = saveStatus === "error";

  return (
    <DialogTrigger defaultOpen={defaultOpen}>
      <Button
        aria-label="Automation details"
        className={twMerge(
          iconButton({ variant: ButtonVariant.Tertiary }),
          "relative text-muted-foreground",
        )}
      >
        {isSlowSaving ? (
          <Loader
            style={LoaderStyle.SPINNER}
            className="mx-0 mb-0 size-5 text-gray-300"
          />
        ) : (
          <IconInfoCircle className="size-5" />
        )}
        {hasError ? (
          <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-red-500 ring-2 ring-white" />
        ) : null}
      </Button>

      <Popover placement="bottom end" className={popoverStyle().popover()}>
        <Dialog className="outline-hidden" aria-label="Automation details">
          <div className="w-64 p-1 text-sm">
            <InfoSection label="Last saved">
              <InfoRow icon={<IconClock className="size-4" />}>
                {isSaving ? (
                  "Saving…"
                ) : hasError ? (
                  <span className="text-red-600">
                    Couldn&apos;t save changes
                  </span>
                ) : (
                  capitalizeFirst(
                    formatRelativeTime(lastSavedAt ?? automation.last_saved_at),
                  )
                )}
              </InfoRow>
            </InfoSection>

            <InfoSection label="Next run">
              <InfoRow icon={<IconPlayerPlay className="size-4" />}>
                {describeAutomationNextRun(automation)}
              </InfoRow>
            </InfoSection>

            <InfoSection label="Created">
              <InfoRow icon={<IconCalendar className="size-4" />}>
                {DateTime.fromISO(automation.created_at).toFormat(
                  "MMM d, yyyy, h:mm a",
                )}
              </InfoRow>
              <InfoRow icon={<IconUser className="size-4" />}>
                {createdByName ?? "—"}
              </InfoRow>
            </InfoSection>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

interface InfoSectionProps {
  label: string;
  children: ReactNode;
}

// Faux menu section header and items, styled to match the real Menu primitives
// so the popover reads as a regular menu even though its rows aren't interactive.
const InfoSection: FC<InfoSectionProps> = (props) => {
  const { label, children } = props;
  return (
    <div>
      <div className={menuStyle().sectionHeader()}>{label}</div>
      {children}
    </div>
  );
};

interface InfoRowProps {
  icon: ReactNode;
  children: ReactNode;
}

const InfoRow: FC<InfoRowProps> = (props) => {
  const { icon, children } = props;
  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-foreground">
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
};
